/* === src/context/AuthContext.js (Fail-Safe Version) === */
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

const DEFAULT_TIMEOUT_MINS = 30; // Default 30 minutes
const GRACE_PERIOD_MS = 30 * 1000; // 30 Seconds warning

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();

    // --- 1. SYNCHRONOUS INITIALIZATION (Fixes Blank Screen) ---
    // We read storage IMMEDIATELY. The app knows if it's logged in before the first render.
    const [token, setToken] = useState(() => localStorage.getItem('token') || sessionStorage.getItem('token'));
    
    const [user, setUser] = useState(() => {
        try {
            const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error("Auth Data Corrupt:", error);
            return null;
        }
    });

    // We start loading as FALSE because we already grabbed the data above.
    // The app will render instantly.
    const [loading, setLoading] = useState(false);
    
    const [unreadCount, setUnreadCount] = useState(0);
    const [idleTimeoutMs, setIdleTimeoutMs] = useState(DEFAULT_TIMEOUT_MINS * 60 * 1000);
    const [showIdleModal, setShowIdleModal] = useState(false);
    
    // Refs
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);

    // --- 2. Background Config Fetch (Non-Blocking) ---
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                // We fetch config in the background. If it fails, we just use defaults.
                // We do NOT block the UI here.
                const { data } = await api.get('/public/config');
                if (data.session_timeout_mins) {
                    const mins = parseInt(data.session_timeout_mins, 10);
                    if (!isNaN(mins) && mins > 0) {
                        setIdleTimeoutMs(mins * 60 * 1000);
                    }
                }
            } catch (err) {
                // Silent fail is okay here, we have defaults.
                console.warn("Using default session timeout.");
            }
        };
        fetchConfig();
    }, []);

    // --- 3. Login / Logout Logic ---
    const logout = useCallback(async (reason = '') => {
        try {
            // 🚀 FIRE AND FORGET LOGOUT HANDSHAKE:
            // This endpoint notifies the backend container to clear the httpOnly cookies cleanly
            await api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) {
            console.warn("Server cookie clearing skipped or unavailable:", err);
        }

        // Clear Local State
        setUser(null);
        setToken(null);
        setShowIdleModal(false);
        setUnreadCount(0);
        
        // Clear Storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        
        // Redirect
        navigate('/auth');

        if (reason) {
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('auth:logout-reason', { detail: reason }));
            }, 500);
        }
    }, [navigate, token]);

    const login = (newToken, userData, rememberMe) => {
        const safeUser = {
            id: userData.id || userData.Id,
            email: userData.email || userData.Email,
            role: userData.role || userData.Role,
            companyId: userData.companyId || userData.CompanyId,
            companyName: userData.companyName || userData.CompanyName,
            fullName: userData.fullName || userData.FullName,
            firstNames: userData.firstNames || userData.FirstNames,
            surname: userData.surname || userData.Surname,
            firstName: userData.firstName || userData.FirstName
        };

        setToken(newToken);
        setUser(safeUser);
        
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', newToken);
        storage.setItem('user', JSON.stringify(safeUser));
        localStorage.setItem('lastActivity', Date.now().toString());

        refreshUnreadCount(newToken);
    };

    // --- 4. Unread Messages Polling ---
    const refreshUnreadCount = useCallback(async (authToken) => {
        const tokenToUse = authToken || token;
        if (!tokenToUse) return;

        try {
            const res = await api.get('/messages/unread-count', { headers: { Authorization: `Bearer ${tokenToUse}` }});
            setUnreadCount(res.data.unreadCount || 0);
        } catch (e) { /* Ignore errors */ }
    }, [token]);

    useEffect(() => {
        if (!token) return;
        refreshUnreadCount(token);
        const interval = setInterval(() => refreshUnreadCount(token), 60000); // 1 min poll
        return () => clearInterval(interval);
    }, [token, refreshUnreadCount]);

    // --- 5. Idle Timer Logic ---
    const recordActivity = useCallback(() => {
        if (!user) return;

        if (!showIdleModal) localStorage.setItem('lastActivity', Date.now().toString());
    }, [user, showIdleModal]);

    const checkInactivity = useCallback(() => {
        if (!user) return;
        
        const lastActivity = parseInt(localStorage.getItem('lastActivity') || Date.now(), 10);
        const elapsed = Date.now() - lastActivity;

        if (elapsed > (idleTimeoutMs + GRACE_PERIOD_MS)) {
            logout("Session expired due to inactivity.");
        } else if (elapsed > idleTimeoutMs && !showIdleModal) {
            setShowIdleModal(true);
        }
    }, [user, idleTimeoutMs, showIdleModal, logout]);

    // Activity Listeners
    useEffect(() => {
        if (!user) return;
        
        const events = ['mousemove', 'keydown', 'click', 'scroll'];
        let throttle;
        
        const handleActivity = () => {
            if (!throttle) {
                recordActivity();
                throttle = setTimeout(() => throttle = null, 1000);
            }
        };

        events.forEach(e => window.addEventListener(e, handleActivity));
        const timer = setInterval(checkInactivity, 5000); // Check every 5s

        return () => {
            events.forEach(e => window.removeEventListener(e, handleActivity));
            clearInterval(timer);
        };
    }, [user, recordActivity, checkInactivity]);

    // Grace Period Countdown
    useEffect(() => {
        if (showIdleModal) {
            logoutTimerRef.current = setTimeout(() => logout("Session expired."), GRACE_PERIOD_MS);
        } else {
            clearTimeout(logoutTimerRef.current);
        }
        return () => clearTimeout(logoutTimerRef.current);
    }, [showIdleModal, logout]);

    const stayActive = () => {
        setShowIdleModal(false);
        recordActivity();
    };

    // --- 6. RENDER (No !loading check blocks) ---
    return (
        <AuthContext.Provider value={{ 
            user, token, login, logout, 
            loading, unreadCount, refreshUnreadCount,
            showIdleModal, stayActive, logoutGracePeriod: GRACE_PERIOD_MS
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);